import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';

import './app.css';

import FormBuilderToolbar from './FormBuilderToolbar.jsx';
import * as variables from './variables';
import './FormBuilder.css';
import { items } from './ToolboxSchema';
import FormElementsEdit from './FormElementsEdit';

import { ReactFormBuilder } from '../FormBuilderLibrary';
import { setFormSchema } from '../FormBuilderLibrary/slice';
import StageAppSelection from '../../pages/StageCreationModal/components/StageAppSelection/StageAppSelection';
import usePermissions from '../../hooks/usePermissions';

const FormBuilder = ({
	formId,
	formInfo,
	isReadyToPublish = false,
	formSchema,
	handleModalClose,
	hidePreviewButton,
	totalForms,
	isTemplate,
	setMetaInfoChanged,
	formTitle,
	programStatus,
	fieldLimit
}) => {
	const _ = require('lodash');
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();

	const url = window.location.href;
	const isAppProcess = url?.includes('application-setup');
	const isReview = searchParams.get('formBuilder') === 'review';

	const reduxFormSchema = useSelector((state) => state.form.formSchema[0]);
	const isReadOnly = useSelector((state) => state.form.isReadOnly);
	const formMetaInfo = useSelector((state) => state.form.formMetaInfo);
	const [data, setData] = useState([]);
	const [refresh, setRefresh] = useState(false);
	const [readOnly, setReadOnly] = useState(isReadOnly);
	const { hasEditPermissions } = usePermissions();

	const onLoad = async () => {
		try {
			let response = reduxFormSchema ? Object.values(reduxFormSchema) : [];
			setData(response || formSchema);
			setRefresh(!refresh);
			let clonedResponse = _.cloneDeep(response || formSchema);
			return clonedResponse;
		} catch (e) {
			console.log(e);
		}
	};

	const onPost = (data) => {
		let newData = _.cloneDeep(data.task_data);
		const hasChildComponents = newData?.some((e) => 'parent_id' in e);
		newData?.forEach((e) => {
			delete e.component;
			e.field_name === undefined &&
				(e.field_name = `${e.group_name.toLowerCase()}_${e.id.toLowerCase()}`);
		});
		if (hasChildComponents) {
			const validChildItems = getActiveChildItems(newData);
			const filtered = newData.filter(
				(e) =>
					// Keep if not a child
					!('parent_id' in e) ||
					// Or keep if a child and still referenced
					('parent_id' in e && validChildItems.includes(e.id || e.field_id))
			);
			dispatch(setFormSchema([0, [...filtered]]));
		} else {
			dispatch(setFormSchema([0, [...newData]]));
		}
	};

	const getActiveChildItems = (data) => {
		const allchildItems = data.filter((e) => 'parent_id' in e).map((e) => e.id || e.field_id);
		const referencedChildIds = data.flatMap((e) =>
			(e.child_items || []).filter((id) => id != null).map((id) => id.toLowerCase())
		);
		const validChildItems = allchildItems.filter((id) =>
			referencedChildIds.includes(id.toLowerCase())
		);

		return validChildItems;
	};

	useEffect(() => {
		const mainAppContainer = document.getElementById('baselayout-box');
		const toolboxContainer = document.getElementById('react-form-builder-toolbar');

		mainAppContainer.addEventListener('scroll', function () {
			const isFixed = toolboxContainer?.classList?.contains('fixedElement');
			mainAppContainer?.scrollTop >= 235
				? toolboxContainer?.classList?.add('fixedElement')
				: isFixed && toolboxContainer?.classList?.remove('fixedElement');
		});
	}, []);

	useEffect(() => {
		setReadOnly(isReadOnly);
	}, [isReadOnly]);

	useEffect(() => {
		onLoad();
	}, [reduxFormSchema]);

	return (
		<>
			<Helmet>
				<link rel="stylesheet" href="https://use.fontawesome.com/releases/v5.13.0/css/all.css" />
				<link
					rel="stylesheet"
					href="https://stackpath.bootstrapcdn.com/bootstrap/4.4.1/css/bootstrap.min.css"
					integrity="sha384-Vkoo8x4CGsO3+Hhxv8T/Q5PaXtkKtu6ug5TOeNV6gBiFeWPGFN9MuhOf23Q9Ifjh"
					crossOrigin="anonymous"
				/>
			</Helmet>

			<div data-testid={'formbuilder'} style={{ width: '100%' }} className="App">
				<FormBuilderToolbar
					data={data}
					variables={variables}
					formID={formId}
					isReadyToPublish={isReadyToPublish}
					handleModalClose={handleModalClose}
					forceRefresh={() => setRefresh(!refresh)}
					hidePreviewButton={hidePreviewButton}
					readOnly={readOnly}
					totalForms={totalForms}
					isTemplate={isTemplate}
					hasEditPermissions={hasEditPermissions}
					setMetaInfoChanged={setMetaInfoChanged}
					programStatus={programStatus}
				/>

				<div style={{ display: 'flex', gap: '20px', width: '100%' }}>
					{isReview && isAppProcess && (
						<StageAppSelection isReviewForm={true} isReadOnly={readOnly} />
					)}

					<ReactFormBuilder
						onLoad={onLoad}
						onPost={onPost}
						refresh={refresh}
						forceRefresh={() => setRefresh(!refresh)}
						toolbarItems={items}
						formId={formId}
						formInfo={formInfo}
						formMetaInfo={formMetaInfo}
						renderEditForm={(props) => <FormElementsEdit {...props} />}
						readOnly={readOnly}
						in_use={false}
						formTitle={formTitle}
						setMetaInfoChanged={setMetaInfoChanged}
						isTemplate={isTemplate}
						hasEditPermissions={hasEditPermissions}
						isReadyToPublish={isReadyToPublish}
						fieldLimit={fieldLimit}
					/>
				</div>
			</div>
		</>
	);
};

export default FormBuilder;
